import { observer } from "mobx-react";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import React, { useEffect, useMemo } from "react";
import { Grid } from "@mui/material";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import { useStore } from "utils/useStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { AuthStore } from "stores/AuthStore";
import { customFilterSelectStyles } from "components/Conversations/Views/Drawer/shared";
import FilterSectionContainer from "components/Conversations/Views/Drawer/FilterSectionContainer";
import { useOptionRestriction } from "../BaseFilterComponents/useRestrictedOptions";
import { usePruneSelectedValues } from "../BaseFilterComponents/usePruneSelectedValues";

const DEFAULT_ALLOWED_MODES = ["include", "exclude"] as const;

export const ConversationLabelingFilter = observer(function (
    props: IApplicationFilterComponent,
) {
    const valuesStore = useStore(ApplicationFilterValuesStore);
    const authStore = useStore(AuthStore);
    const { hasRestrictions, isValueAllowed } = useOptionRestriction({
        optionRestriction: props.optionRestriction,
        restrictedValues: props.restrictedValues,
    });
    const allowedModes =
        props.modeRestriction?.allowedModes ?? DEFAULT_ALLOWED_MODES;
    const modeOptions: Array<{
        label: string;
        value: boolean;
        isDisabled?: boolean;
    }> = [
        {
            label: "is labeled",
            value: true,
            isDisabled: !allowedModes.includes("include"),
        },
        {
            label: "is not labeled",
            value: false,
            isDisabled: !allowedModes.includes("exclude"),
        },
    ];
    useEffect(() => {
        if (!props.modeRestriction) return;
        const currentMode = props.store.containsDataLabels
            ? "include"
            : "exclude";
        if (allowedModes.includes(currentMode)) return;
        props.store.setContainsConversationLabels(allowedModes[0] === "include");
    }, [allowedModes, props.modeRestriction, props.store]);

    usePruneSelectedValues({
        selectedValues: props.store.dataLabels,
        setSelectedValues: (labels) => props.store.setConversationLabels(labels),
        isValueAllowed: (label) => isValueAllowed(label.id),
        enabled: hasRestrictions,
    });

    const availableOptions = useMemo(() => {
        const rawOptions = valuesStore.organizedConversationLabelingOptions ?? [];
        if (!hasRestrictions) return rawOptions;

        return rawOptions
            .map((optionGroupOrLabel: any) => {
                if (Array.isArray(optionGroupOrLabel?.options)) {
                    const filteredOptions = optionGroupOrLabel.options.filter(
                        (option: any) => isValueAllowed(option.id),
                    );
                    if (filteredOptions.length === 0) return null;
                    return {
                        ...optionGroupOrLabel,
                        options: filteredOptions,
                    };
                }

                const optionId = `${optionGroupOrLabel?.id ?? ""}`;
                if (!optionId) return null;
                const isAllowed = isValueAllowed(optionId);

                return isAllowed ? optionGroupOrLabel : null;
            })
            .filter((option): option is any => Boolean(option));
    }, [
        hasRestrictions,
        isValueAllowed,
        valuesStore.organizedConversationLabelingOptions,
    ]);

    if (
        !authStore.canUserView("Data Labeling") &&
        !authStore.canUserView("Research")
    )
        return null;

    return (
        <FilterSectionContainer>
            <Grid container item direction="column">
                <Grid item>
                    <AcxSelectSingle
                        id={"application-filters-is-labeled"}
                        options={modeOptions}
                        labelField="label"
                        valueField="value"
                        defaultValue={{
                            label: props.store.containsDataLabelsString,
                            value: props.store.containsDataLabels === true,
                        }}
                        customStyle={customFilterSelectStyles}
                        onChange={(item) => {
                            if (item.isDisabled) return;
                            props.store.setContainsConversationLabels(
                                item.value,
                            );
                        }}
                        isDisabled={modeOptions.filter((item) => !item.isDisabled).length <= 1}
                    />
                </Grid>
                <Grid
                    item
                    onClick={() => {
                        valuesStore.loadDataLabelingModules();
                    }}
                >
                    <AcxSelectMulti
                        id="application-filters-data-labeling"
                        fullWidth
                        isLoading={valuesStore.getTaskLoading(
                            ApplicationFilterValuesStore.Tasks
                                .LOAD_DATA_LABELING_MODULES,
                        )}
                        options={availableOptions}
                        defaultValue={props.store.dataLabels}
                        onChange={(options) => {
                            props.store.setConversationLabels(
                                !!options ? options : [],
                            );
                        }}
                        valueField="id"
                        labelField="name"
                        containerHeight="auto"
                        maxContainerHeight="96px"
                        alignControls="flex-start"
                        isClearable={true}
                    />
                </Grid>
            </Grid>
        </FilterSectionContainer>
    );
});
